import {
    IconMedication,
    IconCamera,
    IconScissors,
    IconUsers,
    IconCube,
    IconClipboard,
  } from "../components/Home/Icons";
  import { CategoryKey, CategoryConfig } from "../types/home";
  
  // ============================================
  // CATEGORY CONFIG
  // ============================================
  
  export const CATEGORIES: CategoryConfig[] = [
    {
      key: "medication",
      label: "Medications",
      icon: IconMedication,
      gradient: "from-violet-400 to-violet-600",
      bgColor: "bg-violet-50",
      textColor: "text-violet-700",
      borderColor: "border-violet-200",
      lightBg: "bg-violet-500/10",
    },
    {
      key: "imaging",
      label: "Imaging",
      icon: IconCamera,
      gradient: "from-blue-400 to-blue-600",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
      borderColor: "border-blue-200",
      lightBg: "bg-blue-500/10",
    },
    {
      key: "procedure",
      label: "Procedures",
      icon: IconScissors,
      gradient: "from-rose-400 to-rose-600",
      bgColor: "bg-rose-50",
      textColor: "text-rose-700",
      borderColor: "border-rose-200",
      lightBg: "bg-rose-500/10",
    },
    {
      key: "referral",
      label: "Referrals",
      icon: IconUsers,
      gradient: "from-amber-400 to-amber-600",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
      borderColor: "border-amber-200",
      lightBg: "bg-amber-500/10",
    },
    {
      key: "dme",
      label: "DME",
      icon: IconCube,
      gradient: "from-emerald-400 to-emerald-600",
      bgColor: "bg-emerald-50",
      textColor: "text-emerald-700",
      borderColor: "border-emerald-200",
      lightBg: "bg-emerald-500/10",
    },
    {
      key: "other",
      label: "Other",
      icon: IconClipboard,
      gradient: "from-slate-400 to-slate-600",
      bgColor: "bg-slate-50",
      textColor: "text-slate-700",
      borderColor: "border-slate-200",
      lightBg: "bg-slate-500/10",
    },
  ];
  
  // ============================================
  // HELPER FUNCTIONS
  // ============================================
  
  export function getCategoryKey(requestType: string): CategoryKey {
    const type = requestType?.toLowerCase() || "";
    if (type.includes("medication")) return "medication";
    if (type.includes("imaging")) return "imaging";
    if (type.includes("procedure")) return "procedure";
    if (type.includes("referral")) return "referral";
    if (type.includes("dme")) return "dme";
    return "other";
  }
  
  export function getCategoryConfig(requestType: string): CategoryConfig {
    const key = getCategoryKey(requestType);
    return CATEGORIES.find((c) => c.key === key) || CATEGORIES[5];
  }
  
  export function extractPrimaryRequest(
    requestType: string,
    requestDetails: string
  ): string {
    if (!requestDetails) return "";
  
    const type = requestType?.toLowerCase() || "";
  
    const parts: Record<string, string> = {};
    requestDetails.split(";").forEach((part) => {
      const [key, ...valueParts] = part.split(":");
      if (key && valueParts.length > 0) {
        parts[key.trim().toLowerCase()] = valueParts.join(":").trim();
      }
    });
  
    if (type.includes("medication")) {
      const name = parts["medication"] || "";
      const dose = parts["dose"] || "";
      if (name) return dose ? `${name} ${dose}` : name;
    }
  
    if (type.includes("imaging")) {
      const study = parts["study"] || "";
      const region = parts["region"] || "";
      if (study && region) return `${study} ${region}`;
      return study || region || "";
    }
  
    if (type.includes("procedure")) {
      return parts["procedure"] || "";
    }
  
    if (type.includes("referral")) {
      return parts["specialty"] || parts["reason"] || "";
    }
  
    if (type.includes("dme")) {
      return parts["item"] || parts["category"] || "";
    }
  
    return "";
  }
  
  export function formatDate(dateString: string) {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
  
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  }
  
  // ============================================
  // STATUS HELPERS
  // ============================================
  
  import type {
    Case,
    CaseStatus,
    StatusDisplay,
    StatusTone,
    QuickFilterKey,
    SortMode,
    QuickFilterConfig,
  } from "../types/home";
  
  /**
   * Derives case status from available fields.
   * Returns null if status cannot be reliably determined.
   */
  export function getCaseStatus(caseItem: Case): CaseStatus | null {
    // Prefer explicit status field if present
    if (caseItem.status) {
      return caseItem.status;
    }
  
    // Derive from denial_reason: if there's a denial reason, case was denied
    if (caseItem.denial_reason && caseItem.denial_reason.trim().length > 0) {
      return "denied";
    }
  
    // Derive from appeal_status if available
    if (caseItem.appeal_status) {
      const appealStatus = caseItem.appeal_status.toLowerCase();
      if (appealStatus.includes("approved")) return "approved";
      if (appealStatus.includes("denied")) return "denied";
      if (appealStatus.includes("submitted")) return "submitted";
      if (appealStatus.includes("pending") || appealStatus.includes("review")) return "in_progress";
    }
  
    // Derive from is_submitted flag
    if (caseItem.is_submitted === true) {
      return "submitted";
    }
  
    // If we have request details but no other indicators, it's likely in progress
    if (caseItem.request_details && caseItem.request_details.trim().length > 0) {
      return "in_progress";
    }
  
    // If only basic info exists, treat as draft
    if (caseItem.friendly_name && !caseItem.request_type) {
      return "draft";
    }
  
    // Cannot reliably determine status
    return null;
  }
  
  /**
   * Returns display properties for a given status.
   */
  export function getStatusDisplay(status: CaseStatus): StatusDisplay {
    const displays: Record<CaseStatus, StatusDisplay> = {
      draft: { label: "Draft", tone: "neutral" },
      in_progress: { label: "In Progress", tone: "info" },
      submitted: { label: "Submitted", tone: "info" },
      denied: { label: "Denied", tone: "danger" },
      approved: { label: "Approved", tone: "success" },
      needs_info: { label: "Needs Info", tone: "warning" },
    };
    return displays[status];
  }
  
  /**
   * Returns Tailwind classes for status badge based on tone.
   */
  export function getStatusToneClasses(tone: StatusTone): string {
    const toneClasses: Record<StatusTone, string> = {
      neutral: "bg-slate-100 text-slate-600",
      info: "bg-blue-50 text-blue-600",
      warning: "bg-amber-50 text-amber-600",
      danger: "bg-red-50 text-red-600",
      success: "bg-emerald-50 text-emerald-600",
    };
    return toneClasses[tone];
  }
  
  // ============================================
  // QUICK FILTER CONFIG
  // ============================================
  
  export const QUICK_FILTERS: QuickFilterConfig[] = [
    { key: "all", label: "All" },
    { key: "drafts", label: "Drafts" },
    { key: "denied", label: "Denied" },
    { key: "recent", label: "Recent" },
    { key: "starred", label: "Starred", requiresField: "is_starred" },
  ];
  
  /**
   * Returns available quick filters based on whether required fields exist in any case.
   */
  export function getAvailableFilters(cases: Case[]): QuickFilterConfig[] {
    return QUICK_FILTERS.filter((filter) => {
      if (!filter.requiresField) return true;
      // Only show filter if at least one case has the required field
      return cases.some((c) => c[filter.requiresField!] !== undefined);
    });
  }
  
  // ============================================
  // FILTERING & SORTING
  // ============================================
  
  /**
   * Filters cases by search query.
   */
  export function filterByQuery(cases: Case[], query: string): Case[] {
    if (!query.trim()) return cases;
  
    const term = query.toLowerCase();
    return cases.filter((c) => {
      const primaryRequest = extractPrimaryRequest(
        c.request_type,
        c.request_details
      ).toLowerCase();
      const searchableText = [
        c.friendly_name,
        c.request_type,
        c.request_details,
        c.insurance_company,
        c.denial_reason,
        c.diagnoses,
        primaryRequest,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
  
      return searchableText.includes(term);
    });
  }
  
  /**
   * Filters cases by quick filter selection.
   */
  export function filterByQuickFilter(
    cases: Case[],
    filter: QuickFilterKey
  ): Case[] {
    switch (filter) {
      case "all":
        return cases;
  
      case "drafts":
        return cases.filter((c) => {
          const status = getCaseStatus(c);
          return status === "draft" || status === null;
        });
  
      case "denied":
        return cases.filter((c) => {
          const status = getCaseStatus(c);
          return status === "denied";
        });
  
      case "recent": {
        // Recent = created/updated in the last 7 days
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return cases.filter((c) => {
          const dateToCheck = c.updated_at || c.created_at;
          return new Date(dateToCheck) >= sevenDaysAgo;
        });
      }
  
      case "starred":
        return cases.filter((c) => c.is_starred === true);
  
      default:
        return cases;
    }
  }
  
  /**
   * Sorts cases by the selected sort mode.
   */
  export function sortCases(cases: Case[], sortMode: SortMode): Case[] {
    const sorted = [...cases];
  
    switch (sortMode) {
      case "recent":
        return sorted.sort((a, b) => {
          const dateA = new Date(a.updated_at || a.created_at).getTime();
          const dateB = new Date(b.updated_at || b.created_at).getTime();
          return dateB - dateA;
        });
  
      case "oldest":
        return sorted.sort((a, b) => {
          const dateA = new Date(a.updated_at || a.created_at).getTime();
          const dateB = new Date(b.updated_at || b.created_at).getTime();
          return dateA - dateB;
        });
  
      case "alpha":
        return sorted.sort((a, b) => {
          const titleA = extractPrimaryRequest(a.request_type, a.request_details) || a.request_type || "";
          const titleB = extractPrimaryRequest(b.request_type, b.request_details) || b.request_type || "";
          return titleA.localeCompare(titleB);
        });
  
      case "insurer":
        return sorted.sort((a, b) => {
          const insurerA = a.insurance_company || "";
          const insurerB = b.insurance_company || "";
          return insurerA.localeCompare(insurerB);
        });
  
      default:
        return sorted;
    }
  }
  
  /**
   * Combined filter and sort function.
   */
  export function filterAndSortCases(
    cases: Case[],
    options: { query: string; quickFilter: QuickFilterKey; sortMode: SortMode }
  ): Case[] {
    let result = cases;
  
    // Apply search query filter
    result = filterByQuery(result, options.query);
  
    // Apply quick filter
    result = filterByQuickFilter(result, options.quickFilter);
  
    // Apply sort
    result = sortCases(result, options.sortMode);
  
    return result;
  }
  
  /**
   * Groups cases by category.
   */
  export function groupCasesByCategory(
    cases: Case[]
  ): Record<CategoryKey, Case[]> {
    const groups: Record<CategoryKey, Case[]> = {
      medication: [],
      imaging: [],
      procedure: [],
      referral: [],
      dme: [],
      other: [],
    };
  
    cases.forEach((c) => {
      const key = getCategoryKey(c.request_type);
      groups[key].push(c);
    });
  
    return groups;
  }